import { useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { RootState } from '../../store';
import {
  useGetNewCounsellingQuery,
  useGetLiveCounsellingStatusQuery,
  useGetSubjectQuery,
  useGetStaffQuery,
} from './counselling.services';
import { exportToExcel, ExcelColumn } from '../../shared/export/excel';

/**
 * Migrated from Angular `CounsellingLiveUpdatesComponent`
 * (src/app/views/pages/counselling/counselling-live-updates).
 *
 * Route: /counselling/live-updates
 */

// ── Column definitions ───────────────────────────────────────────────────────

interface Col {
  field: string;
  header: string;
}

const COLUMNS: Col[] = [
  { field: 'seniority_state', header: 'Seniority' },
  { field: 'oldDisName', header: 'District' },
  { field: 'oldBlkNme', header: 'Block' },
  { field: 'teacher_id', header: 'Teacher ID' },
  { field: 'name', header: 'Teacher Name' },
  { field: 'desgnation', header: 'Designation' },
  { field: 'subject', header: 'Subject' },
  { field: 'newDesignation', header: 'New Designation' },
  { field: 'newSubject', header: 'New Subject' },
  { field: 'NewDis', header: 'New District' },
  { field: 'NewBlk', header: 'New Block' },
  { field: 'statusLabel', header: 'Status' },
];

// ── Helper: resolve teacher status label ─────────────────────────────────────

function getTeacherStatusLabel(status: string): string {
  switch (status) {
    case '1':
      return 'Transfer/Promotion';
    case '2':
      return 'Absent';
    case '3':
      return 'Not Willing/Relinquish';
    default:
      return status || '-';
  }
}

// ── Helper: filter counselling list by role (same as Angular) ─────────────────

function filterCounsellingByRole(
  result: any[],
  userTypeId: string,
  userTypeId1: string
): any[] {
  // var filterCnsn = res.result.filter((ft) => ft.Status == '1')

  if (userTypeId === '5' && userTypeId1 === '1') {
    return result.filter((ft) => ft.Status === '1' && ft.Directorate === '1'); // DEE
  } else if (userTypeId === '5' && userTypeId1 === '2') {
    return result.filter((ft) => ft.Status === '1' && ft.Directorate === '2'); // DSE
  } else if (userTypeId === '5' && userTypeId1 === '3') {
    // var filterCnsn = res.result.filter(ft => ft.Status == '1') // STATE
    return result;
  } else if (userTypeId === '5' && userTypeId1 === '29') {
    return result.filter((ft) => ft.Status === '1' && ft.Directorate === '4'); // GCC
  } else if (userTypeId === '5' && userTypeId1 === '6') {
    return result.filter((ft) => ft.Status === '1' && ft.Directorate === '5'); // SCERT
  } else if (userTypeId === '5' && userTypeId1 === '8') {
    return result.filter((ft) => ft.Status === '1' && ft.Directorate === '6'); // SCERT
  } else {
    return result.filter((ft) => ft.Status === '1');
  }
}

// ── Component ────────────────────────────────────────────────────────────────

const LiveUpdates = () => {
  const { usertypeid, emisUsertype1, loaded } = useSelector(
    (state: RootState) => state.session
  );

  // Selected counselling
  const [counsellingId, setCounsellingId] = useState<string | null>(null);
  // Whether user has clicked "View Updates"
  const [showTable, setShowTable] = useState(false);
  // Global filter
  const [globalFilter, setGlobalFilter] = useState('');
  // Column chooser
  const [selectedCols, setSelectedCols] = useState<Col[]>(COLUMNS);

  const dtRef = useRef<DataTable<any[]>>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  // Counselling list dropdown
  const { data: counsellingRaw } = useGetNewCounsellingQuery();

  // Subject & StaffType JSON for label resolution
  const { data: subjectRaw } = useGetSubjectQuery();
  const { data: staffRaw } = useGetStaffQuery();

  // Live updates data — only fetch when user clicks submit
  const { data: liveRaw, isFetching: liveLoading } =
    useGetLiveCounsellingStatusQuery(
      { counsellingId: counsellingId },
      { skip: !loaded || !showTable || !counsellingId }
    );

  // ── Derived data ─────────────────────────────────────────────────────────

  const counsellingList = useMemo(() => {
    if (!counsellingRaw?.dataStatus) return [];
    const filtered = filterCounsellingByRole(
      counsellingRaw.result,
      usertypeid,
      emisUsertype1
    );
    return filtered.map((item: any) => ({
      label: item.Title,
      value: item.IndexId,
    }));
  }, [counsellingRaw, usertypeid, emisUsertype1]);

  const subjectList: { value: string; label: string }[] = useMemo(() => {
    if (!subjectRaw) return [];
    return subjectRaw.AllSubject || subjectRaw;
  }, [subjectRaw]);

  const staffTypeList: { value: string; label: string }[] = useMemo(() => {
    if (!staffRaw) return [];
    return staffRaw.AllStaffType || staffRaw.StaffType || staffRaw;
  }, [staffRaw]);

  const tableData = useMemo(() => {
    if (!liveRaw?.dataStatus) return [];

    const counsellingLabel =
      counsellingList.find((c) => c.value === counsellingId)?.label || '';

    return liveRaw.result.map((row: any) => {
      const subMatch = subjectList.find((s) => s.value === row.subject);
      const newSubMatch = subjectList.find((s) => s.value === row.new_subject);
      const desMatch = staffTypeList.find((s) => s.value === row.desgnation);
      const newDesMatch = staffTypeList.find(
        (s) => s.value === row.new_designation
      );

      return {
        ...row,
        subject: subMatch ? subMatch.label : row.subject || '-',
        newSubject: newSubMatch ? newSubMatch.label : '',
        desgnation: desMatch ? desMatch.label : row.desgnation || '-',
        newDesignation: newDesMatch ? newDesMatch.label : '',
        CounsellingLabel: counsellingLabel,
        statusLabel: getTeacherStatusLabel(row.teacher_status),
      };
    });
  }, [liveRaw, subjectList, staffTypeList, counsellingList, counsellingId]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleExportExcel = useCallback(() => {
    if (!tableData.length) return;

    const excelData = tableData.map((row: any) => ({
      District: row.oldDisName || '-',
      Block: row.oldBlkNme || '-',
      Counselling: row.CounsellingLabel || '-',
      'Teacher ID': row.teacher_id || '-',
      Name: row.name || '-',
      Gender: row.gender || '-',
      'UDISE Code': row.udise_code || '-',
      Priority: row.priority || '-',
      'Vacancy/Need': row.vacNeedFlag === '1' ? 'Need' : 'Vacancy',
      'School Name': row.school_name || '-',
      Designation: row.desgnation || '-',
      Subject: row.subject || '-',
      Seniority: +row.seniority_state || '-',
      Status: row.statusLabel || '-',
      'New Designation': row.newDesignation || '-',
      'New Subject': row.newSubject || '-',
      'New School Name': row.newSchlNme || '-',
      'New District': row.NewDis || '-',
      'New Block': row.NewBlk || '-',
    }));

    const columns: ExcelColumn[] = Object.keys(excelData[0]).map((key) => ({
      key,
      label: key,
    }));

    exportToExcel({
      data: excelData,
      columns,
      fileName: `Counselling Live Update_export_${Date.now()}`,
      sheetName: 'Counselling Live Update',
    });
  }, [tableData]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="card p-4">
        <div className="row align-items-end">
          <div className="col-lg-4 col-12 mb-2">
            <label className="fw-bold d-block mb-1">Counselling</label>
            <Dropdown
              value={counsellingId}
              options={counsellingList}
              optionLabel="label"
              optionValue="value"
              placeholder="Select counselling.."
              filter
              filterBy="label"
              showClear
              className="w-100"
              onChange={(e) => {
                setCounsellingId(e.value);
                if (e.value) {
                  setShowTable(true);
                } else {
                  setShowTable(false);
                }
              }}
            />
          </div>
          <div className="col-lg-8 col-12 mb-2 text-end">
            <Button
              type="button"
              label="Export As Excel"
              icon="pi pi-file-excel"
              className="p-button-success p-button-sm rounded"
              onClick={handleExportExcel}
              disabled={tableData.length === 0}
            />
          </div>
        </div>

        {showTable && (
          <div className="mt-3">
            <DataTable
              ref={dtRef}
              value={tableData}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={liveLoading}
              showGridlines
              resizableColumns
              rowHover
              stripedRows
              dataKey="teacher_id"
              globalFilter={globalFilter}
              globalFilterFields={selectedCols.map((c) => c.field)}
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              emptyMessage="No Records Found"
              tableStyle={{ minWidth: '80rem' }}
              header={
                <div className="row">
                  <div className="col-md-3 col-12 mb-2">
                    <MultiSelect
                      value={selectedCols}
                      options={COLUMNS}
                      optionLabel="header"
                      filter
                      filterBy="header"
                      onChange={(e) => {
                        const selected = COLUMNS.filter((col) =>
                          e.value.includes(col)
                        );
                        setSelectedCols(selected);
                      }}
                      selectedItemsLabel="{0} columns selected"
                      placeholder="Choose Columns"
                      className="w-100"
                    />
                  </div>
                  <div className="col-md-9 col-12 text-end">
                    <span className="p-input-icon-left">
                      <InputText
                        type="text"
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search keyword"
                      />
                    </span>
                  </div>
                </div>
              }
            >
              {selectedCols.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable
                  // filter
                  filterPlaceholder="Search"
                  body={(row) => row[col.field] || '-'}
                  style={{ minWidth: '8rem' }}
                />
              ))}
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveUpdates;
